// WalmartRoutersDashboard.js
// Router Fleet Status table (polling only; no sockets).
// Inline "Location" editing, delete row, and renders ARP (GW IP/MAC).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getSocket, initiateSocket } from './socket';
import GatewayHealthModal from './GatewayHealthModal';
import AdbTimeMonitorModal from './AdbTimeMonitorModal';
import AddDeviceModal from './AddDeviceModal';
import ProtectedComponent from './ProtectedComponent';

/* ───────────────────── helpers ───────────────────── */
function s(v) {
  return v === null || v === undefined ? '' : String(v);
}
function fmtDate(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  return d.toLocaleString();
}
function upsertByKey(list, row, key = 'serialnum') {
  const idx = list.findIndex(r => s(r[key]) === s(row[key]));
  if (idx === -1) return [...list, row];
  const next = [...list];
  next[idx] = { ...next[idx], ...row };
  return next;
}


const formatCurrSim = (v) => (v === '0' || v === 0) ? 'SIM 1'
                       : (v === '1' || v === 1) ? 'SIM 2'
                       : '';

/* ───────────────────── company extraction ───────────────────── */
function extractCompanyName(routerName) {
  if (!routerName) return 'Unknown';
  // Extract everything before "LTE" (case insensitive)
  const match = routerName.match(/^(.+?)\s*LTE/i);
  if (match && match[1]) {
    return match[1].trim();
  }
  return 'Unknown';
}

/* ───────────────────── sorting helpers ───────────────────── */
const SORT_KEYS = {
  'Router Name': 'routerName',
  'Serial': 'serialnum',
  'Location': 'location',
  'Router IP': 'lanIp',
  'GW IP': 'gwip',
  'GW MAC': 'gwmac',
  'LTE IP': 'lteIp',
  'Provider': 'provider',
  'IMEI': 'imei',
  'Current SIM Slot': 'currSim',
  'SIM': 'iccid',
  'Phone': 'phone',
  'Signal': 'signal',
  'Status': 'status',
  'Uptime': 'uptime',
  'Last Updated': 'lastUpdated',
};

function compareRows(a, b, key, dir) {
  let va = a[key], vb = b[key];
  if (va == null) va = '';
  if (vb == null) vb = '';
  // numeric-ish comparison for lastUpdated (epoch ms)
  if (key === 'lastUpdated') {
    return dir === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
  }
  const sa = String(va).toLowerCase();
  const sb = String(vb).toLowerCase();
  if (sa < sb) return dir === 'asc' ? -1 : 1;
  if (sa > sb) return dir === 'asc' ? 1 : -1;
  return 0;
}

/* ───────────────────── component ───────────────────── */
export default function WalmartRoutersDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [selectedRouter, setSelectedRouter] = useState(null);
  const [showAdbMonitor, setShowAdbMonitor] = useState(false);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [activeTab, setActiveTab] = useState('Walmart');
  const [sortKey, setSortKey] = useState(null);   // field name or null
  const [sortDir, setSortDir] = useState('asc');   // 'asc' | 'desc'
  const pollRef = useRef(null);
  const hasLoadedOnce = useRef(false);

  // quick lookup of current row by serial
  const bySerial = useMemo(() => {
    const m = new Map();
    rows.forEach(r => m.set(s(r.serialnum), r));
    return m;
  }, [rows]);

  // Extract unique company names from router names and sort alphabetically
  const companies = useMemo(() => {
    const companySet = new Set();
    rows.forEach(r => {
      const company = extractCompanyName(r.routerName);
      companySet.add(company);
    });
    return Array.from(companySet).sort();
  }, [rows]);

  // Filter rows by active tab, then sort
  const filteredRows = useMemo(() => {
    const filtered = rows.filter(r => extractCompanyName(r.routerName) === activeTab);
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => compareRows(a, b, sortKey, sortDir));
  }, [rows, activeTab, sortKey, sortDir]);

  function handleSort(label) {
    const key = SORT_KEYS[label];
    if (!key) return;
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  // Set active tab to Walmart on first load if Walmart exists
  useEffect(() => {
    if (companies.length > 0 && !companies.includes(activeTab)) {
      // If current active tab doesn't exist in companies, switch to first available or Walmart
      if (companies.includes('Walmart')) {
        setActiveTab('Walmart');
      } else {
        setActiveTab(companies[0]);
      }
    }
  }, [companies, activeTab]);

  async function load() {
    try {
      const r = await fetch('/api/router/status', {
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setRows(Array.isArray(data) ? data : []);
      hasLoadedOnce.current = true;
    } catch (e) {
      console.error('load failed', e);
      // Only show loading state if we've never loaded successfully
      if (!hasLoadedOnce.current) {
        setLoading(true);
      }
    }
  }

  async function saveLocation(serial, newLoc) {
    const key = s(serial);
    const prev = bySerial.get(key)?.location || '';
    // optimistic
    setRows(old => upsertByKey(old, { serialnum: key, location: newLoc }));
    setSaving(old => ({ ...old, [key]: true }));
    try {
      const r = await fetch(`/api/router/location/${encodeURIComponent(key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ location: newLoc }),
      });
      if (!r.ok) throw new Error(await r.text());
      const body = await r.json();
      const loc = s(body?.location ?? newLoc);
      setRows(old => upsertByKey(old, { serialnum: key, location: loc }));
    } catch (e) {
      console.error('saveLocation', e);
      setRows(old => upsertByKey(old, { serialnum: key, location: prev }));
      alert('Failed to save location');
    } finally {
      setSaving(old => ({ ...old, [key]: false }));
    }
  }

  async function deleteRow(serial) {
    const key = s(serial);
    const prev = bySerial.get(key);
    // optimistic remove
    setRows(old => old.filter(r => s(r.serialnum) !== key));
    try {
      const r = await fetch(`/api/router/${encodeURIComponent(key)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(await r.text());
    } catch (e) {
      console.error('delete', e);
      if (prev) setRows(old => upsertByKey(old, prev));
      alert('Failed to delete row');
    }
  }

  async function handleRouterNameClick(router) {
    console.log('[RouterFleet] Router clicked:', router.routerName);
    setSelectedRouter(router);

    try {
      // Check if this router has any monitored devices
      const response = await fetch('/api/monitored-devices', {
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch monitored devices');
      }

      const data = await response.json();
      const devicesForRouter = data.devices.filter(d => d.routerName === router.routerName);

      if (devicesForRouter.length === 0) {
        // No devices for this router - show add device modal
        console.log('[RouterFleet] No devices found, showing add device modal');
        setShowAddDevice(true);
      } else {
        // Has devices - show ADB monitor modal
        console.log('[RouterFleet] Found devices, showing ADB monitor');
        setShowAdbMonitor(true);
      }
    } catch (err) {
      console.error('[RouterFleet] Error checking devices:', err);
      alert('Failed to check monitored devices');
    }
  }

  function handleDeviceAdded(device) {
    console.log('[RouterFleet] Device added, opening ADB monitor');
    setShowAddDevice(false);
    setShowAdbMonitor(true);
  }

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 10_000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Initialize WebSocket and listen for real-time router status updates
  useEffect(() => {
    initiateSocket(); // Ensure socket is initialized
    const socket = getSocket();
    if (!socket) return;

    const handleRouterStatusUpdate = (data) => {
      console.log('[RouterFleet] WebSocket update received:', data);

      // If we receive individual router data, update just that router
      if (data.type === 'router-update' && data.router) {
        setRows(old => upsertByKey(old, data.router, 'serialnum'));
      } else {
        // Fallback: reload all data for generic updates
        load();
      }
    };

    socket.on('router-status', handleRouterStatusUpdate);

    return () => {
      socket.off('router-status', handleRouterStatusUpdate);
    };
  }, []);

  return (
    <ProtectedComponent functionCode="OP06" functionName="Router Fleet Status">
      <div className="min-h-screen w-full px-2 sm:px-6 py-4 sm:py-6 bg-[#0d1623] text-white">
      <h1 className="text-2xl sm:text-3xl font-extrabold mb-4 sm:mb-6">Router Fleet Status</h1>

      {loading && <div className="mb-4 opacity-80">Loading…</div>}

      {/* Company Tabs */}
      {companies.length > 0 && (
        <div style={{ marginBottom: '-2px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {companies.map(company => (
            <button
              key={company}
              onClick={() => setActiveTab(company)}
              style={{
                padding: '12px 24px',
                fontWeight: 'bold',
                fontSize: '15px',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                borderBottomLeftRadius: '0',
                borderBottomRightRadius: '0',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                backgroundColor: activeTab === company ? '#1a3a52' : '#0d1623',
                color: activeTab === company ? '#ffffff' : '#6b7280',
                position: 'relative',
                borderBottom: activeTab === company ? '4px solid #3b82f6' : 'none',
                boxShadow: activeTab === company ? '0 2px 8px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== company) {
                  e.target.style.backgroundColor = '#1a2332';
                  e.target.style.color = '#9ca3af';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== company) {
                  e.target.style.backgroundColor = '#0d1623';
                  e.target.style.color = '#6b7280';
                }
              }}
            >
              {company} ({rows.filter(r => extractCompanyName(r.routerName) === company).length})
            </button>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg sm:rounded-2xl shadow-lg -mx-2 sm:mx-0" style={{ borderTopLeftRadius: '0' }}>
        <table className="min-w-full border-separate border-spacing-0" style={{ minWidth: '1200px' }}>
          <thead>
            <tr className="bg-[#152235] text-left text-sm uppercase tracking-wide">
              {['Router Name','Serial','Location','Router IP','GW IP','GW MAC','LTE IP','Provider','IMEI','Current SIM Slot','SIM','Phone','Signal','Status','Uptime','Last Updated'].map(label => (
                <Th key={label} sortable sortKey={SORT_KEYS[label]} activeSortKey={sortKey} sortDir={sortDir} onSort={() => handleSort(label)}>
                  {label}
                </Th>
              ))}
              <Th className="text-center">Del</Th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && !loading ? (
              <tr>
                <Td colSpan={16} className="text-center py-8 opacity-75">
                  No data for {activeTab}.
                </Td>
              </tr>
            ) : (
              filteredRows.map((r) => (
                <Row
                  key={s(r.serialnum) || s(r.routerName)}
                  row={r}
                  saving={!!saving[s(r.serialnum)]}
                  onCommit={(val) => saveLocation(r.serialnum, val)}
                  onDelete={() => deleteRow(r.serialnum)}
                  setSelectedGateway={setSelectedGateway}
                  onRouterNameClick={handleRouterNameClick}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedGateway && (
        <GatewayHealthModal
          gwip={selectedGateway.gwip}
          gwmac={selectedGateway.gwmac}
          onClose={() => setSelectedGateway(null)}
        />
      )}

      {showAddDevice && selectedRouter && (
        <AddDeviceModal
          routerName={selectedRouter.routerName}
          onClose={() => {
            setShowAddDevice(false);
            setSelectedRouter(null);
          }}
          onDeviceAdded={handleDeviceAdded}
        />
      )}

      {showAdbMonitor && selectedRouter && (
        <AdbTimeMonitorModal
          routerName={selectedRouter.routerName}
          onClose={() => {
            setShowAdbMonitor(false);
            setSelectedRouter(null);
          }}
        />
      )}
    </div>
    </ProtectedComponent>
  );
}

/* ───────────────────── presentational ───────────────────── */

function Th({ children, className = '', sortable, sortKey, activeSortKey, sortDir, onSort }) {
  const isActive = sortable && sortKey === activeSortKey;
  return (
    <th
      className={`sticky top-0 z-[1] px-2 sm:px-4 py-3 sm:py-4 border-b border-[#23314a] font-semibold whitespace-nowrap ${sortable ? 'cursor-pointer select-none hover:bg-[#1a3050]' : ''} ${className}`}
      style={{ fontSize: '15px' }}
      onClick={sortable ? onSort : undefined}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortable && (
          <span style={{ opacity: isActive ? 1 : 0.3, fontSize: '12px' }}>
            {isActive && sortDir === 'desc' ? '\u25BC' : '\u25B2'}
          </span>
        )}
      </span>
    </th>
  );
}

function Td({ children, className = '', colSpan }) {
  return (
    <td colSpan={colSpan} className={`px-2 sm:px-4 py-3 sm:py-4 border-b border-[#1f2b40] ${className}`} style={{ fontSize: '15px' }}>
      {children}
    </td>
  );
}

function Row({ row, saving, onCommit, onDelete, setSelectedGateway, onRouterNameClick }) {
  const [loc, setLoc] = useState(row.location || '');
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setLoc(row.location || ''); }, [row.location]);
  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);

  const statusColor =
    row.status === 'connected'
      ? 'text-green-400'
      : row.status === 'disconnected'
      ? 'text-red-400'
      : 'opacity-80';

  return (
    <tr className="odd:bg-[#101b2b] even:bg-[#0f1a29] hover:bg-[#132038] transition-colors">
      <Td>
        {row.routerName ? (
          <span
            style={{ cursor: 'pointer', color: '#4a7dff', textDecoration: 'underline' }}
            onClick={() => onRouterNameClick(row)}
            title="Click to manage ADB time monitoring"
          >
            {row.routerName}
          </span>
        ) : (
          '—'
        )}
      </Td>
      <Td className="font-mono">{row.serialnum || '—'}</Td>

      <Td>
        <div className="flex items-center gap-1 sm:gap-2 min-w-[120px]">
          {editing ? (
            <input
              ref={inputRef}
              className="w-full px-1 sm:px-2 py-1 text-xs sm:text-sm rounded-md bg-[#0c1422] border border-[#2a3a56] outline-none focus:border-[#4a7dff]"
              value={loc}
              onChange={(e) => setLoc(e.target.value)}
              onBlur={() => { setEditing(false); onCommit(loc.trim()); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { setEditing(false); onCommit(loc.trim()); }
                if (e.key === 'Escape') { setEditing(false); setLoc(row.location || ''); }
              }}
              placeholder="Enter location…"
              maxLength={120}
            />
          ) : (
            <button
              type="button"
              className="text-left w-full px-1 sm:px-2 py-1 rounded-md border border-transparent hover:border-[#2a3a56] bg-[#0c1422] truncate"
              onClick={() => setEditing(true)}
              title="Click to edit"
            >
              {row.location ? row.location : <span className="opacity-50">—</span>}
            </button>
          )}
          {saving && <span className="text-xs opacity-70 whitespace-nowrap">Saving…</span>}
        </div>
      </Td>

      <Td className="font-mono">{row.lanIp || '—'}</Td>
      <Td className="font-mono">{row.gwip || 'No Device'}</Td>
      <Td className="font-mono">
        {row.gwmac && row.gwip ? (
          <span
            style={{ cursor: 'pointer', color: '#3498db', textDecoration: 'underline' }}
            onClick={() => setSelectedGateway({ gwip: row.gwip, gwmac: row.gwmac })}
            title="Click to view gateway health"
          >
            {row.gwmac}
          </span>
        ) : (
          'No Device'
        )}
      </Td>
      <Td className="font-mono">{row.lteIp || '—'}</Td>
      <Td>{row.provider || '—'}</Td>
      <Td className="font-mono">{row.imei || '—'}</Td>
      <td>{formatCurrSim(row.currSim)}</td>
      <Td className="font-mono">{row.iccid || '—'}</Td>
      <Td className="font-mono">{row.phone || '—'}</Td>
      <Td>{row.signal || '—'}</Td>
      <Td className={statusColor}>{row.status || '—'}</Td>
      <Td>{row.uptime || '—'}</Td>
      <Td>{row.lastUpdated ? fmtDate(row.lastUpdated) : '—'}</Td>



      <Td className="text-center">
        <button
          type="button"
          className="px-2 py-1 rounded bg-[#3a1f2b] hover:bg-[#53293b]"
          title="Delete row"
          onClick={onDelete}
        >
          ✖
        </button>
      </Td>
    </tr>
  );
}
